import React, { useEffect, useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import { Divider, Tab, Tabs, Typography } from '@mui/material'
import { getObject } from '../../../api/apiClientCrud'
import { useAuth } from '../../../context/AuthContext'
import { useLocation, useOutletContext } from 'react-router-dom'
import Toolbar from '../../toolbar/Toolbar'
import { BiSolidBusiness } from 'react-icons/bi'
import { BsPersonFill } from 'react-icons/bs'
import { TbPhoneFilled, TbMailFilled } from 'react-icons/tb'
import { FaLocationDot } from 'react-icons/fa6'
import Loading from '../../common/Loading'
import NotFound from '../../common/NotFound'
import DOMPurify from 'dompurify'
import ReactImageGallery from 'react-image-gallery'
import 'react-image-gallery/styles/css/image-gallery.css'
import './product.css'
import MyTable from '../../fields/table/MyTable'

const sortImagesByPosition = (attachments = []) =>
  [...attachments].sort((left, right) => {
    const leftPosition = Number(left?.position)
    const rightPosition = Number(right?.position)
    const safeLeft = Number.isFinite(leftPosition) ? leftPosition : Number.MAX_SAFE_INTEGER
    const safeRight = Number.isFinite(rightPosition) ? rightPosition : Number.MAX_SAFE_INTEGER

    return safeLeft - safeRight
  })

function ViewProduct() {
  const { user } = useAuth()
  const userRight = useOutletContext()
  const location = useLocation()
  const productId = location?.state?.id
  const [tabId, setTabId] = useState(0)

  const [product, setProduct] = useState(null)
  const productOptionsTableOptions = []
  const productOptionsTableColumns = []
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!productId) {
      setLoading(false)
      return
    }

    const loadData = async () => {
      try {
        const [productRes] = await Promise.all([getObject('products', productId)])
        setProduct(productRes.data)
      } catch (error) {
        toast.error('Erreur lors du chargement', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [productId])

  const handleChangeTab = (event, newValue) => {
    setTabId(newValue)
  }

  const DescriptionViewer = (htmlContent) => {
    // Nettoyage du contenu avant affichage
    const sanitizedConfig = { __html: DOMPurify.sanitize(htmlContent) }

    return <div dangerouslySetInnerHTML={sanitizedConfig} />
  }

  const mapAttachmentsToGalleryWithBlobs = (product) => {
    if (!product || !Array.isArray(product.attachments)) {
      return []
    }

    return sortImagesByPosition(product.attachments.filter((att) => att?.type === 'image')).map((att) => ({
      original: att.url, // Use the URL served by your backend
      thumbnail: att.url // You can add separate thumbnail URL if you generate them
      //description: att.filename
    }))
  }

  const images = useMemo(() => mapAttachmentsToGalleryWithBlobs(product), [product])

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        return `Produit ${product.name}`
      },
      objectType: 'products',
      actions: { edit: true, audits: true, back: true, list: true, files: true }
    }),
    [userRight, product]
  )

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!product?.id) return <NotFound title='Produit introuvable' />

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', overflowY: 'auto', flexGrow: 1 }}
    >
      <Toolbar options={toolbar} object={product} />

      <Paper sx={{ width: '90%', marginLeft: '5%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', margin: '2%' }}>
          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', marginRight: '5px' }}>
            {product.attachments.length > 0 ? (
              <ReactImageGallery
                items={images}
                thumbnailPosition='left'
                showPlayButton={false}
                showFullscreenButton={false}
                showBullets={false}
                useBrowserFullscreen={false}
                additionalClass='custom-gallery'
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 400,
                  width: '100%',
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                  overflow: 'hidden', // Strictly clips anything that tries to grow too big
                  boxShadow: 3,
                  position: 'relative',
                  '& .image-gallery': {
                    width: '100%',
                    height: '100%'
                  }
                }}
              >
                <Typography component='span'>Aucune Image</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
            <Box sx={{ flex: 1, marginRight: '5px' }}>
              <Typography variant='h4' component='h1' gutterBottom>
                {product.name}
              </Typography>
              {product.reference}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
                <Typography variant='h5' color='primary' component='span' sx={{ mr: 2 }}>
                  {'Coût:'} {product.cost}
                  {'€'}
                </Typography>
                <Typography variant='h5' color='primary' component='span' sx={{ mr: 2 }}>
                  {'Marge:'} {product.margin}
                </Typography>
                <Typography variant='h5' color='primary' component='span' sx={{ mr: 2 }}>
                  {'Prix de vente:'} {product.cost * product.margin}
                  {'€'}
                </Typography>
              </Box>
              <Typography
                component='span'
                sx={{ mr: 2 }}
                color={product.stock > 0 || product.isBuildable ? 'success.main' : 'error.main'}
              >
                {product.stock > 0 || product.isBuildable ? 'Disponible' : 'Indisponible'}
              </Typography>
              {product.isCustomizable && (
                <Typography component='span' color={'success.main'}>
                  Personnalisable
                </Typography>
              )}
              <Typography sx={{ my: 2 }}>{DescriptionViewer(product.shortDescription)}</Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{}}>
              <Tabs value={tabId} onChange={handleChangeTab} aria-label='details tabs'>
                <Tab label='Description' id='descriptionTab' />
                <Tab label='Détails Techniques' id='detailsTab' />
              </Tabs>
            </Box>
            <div role='tabpanel' hidden={tabId !== 0} id={`simple-tabpanel-1`} aria-labelledby={`simple-tab-1`}>
              <Box sx={{ p: 2 }}>{DescriptionViewer(product.description)}</Box>
            </div>

            <div role='tabpanel' hidden={tabId !== 1} id={`simple-tabpanel-2`} aria-labelledby={`simple-tab-2`}>
              <Box sx={{ p: 2 }}>{DescriptionViewer(product.technicalDetails)}</Box>
            </div>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <MyTable
          data={product.options}
          tableOptions={productOptionsTableOptions}
          columns={productOptionsTableColumns}
        />
      </Paper>
    </Box>
  )
}

export default ViewProduct
